/*
 * MIT License
 *
 * Copyright (c) 2023 Mario Döring
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { markIntentionalReload } from '@/func/intentionalReload';
import translator, { currentLanguage } from '@/locale/translator';
import { HmacSHA256, SHA256, enc, MD5 } from 'crypto-js';
import { useToast } from 'vue-toastification';
import type { User } from '@/types/types';

// mirror App\Action\LEADAction::UNAUTHORIZED_CODE_* in the PJAS backend
const UNAUTHORIZED_CODE_NOT_AUTHENTICATED = 401_01;
const UNAUTHORIZED_CODE_LOGIN_REQUIRED = 401_02;

// mirror App\Auth\SessionManager::SIGNATURE_VERSION_BODY_HASH in the PJAS backend
const SIGNATURE_VERSION_BODY_HASH = 1;

export class ClientError extends Error {
  response: Response;
  responseJson: ResponseData;

  constructor(status: number, response: Response, responseJson: ResponseData) {
    super('Request failed with status code ' + status);

    this.name = 'ClientError';
    this.response = response;
    this.responseJson = responseJson;
  }
}

/** error codes for states the app already knows about, which must not produce a default toast */
const QUIET_ERROR_CODES: ReadonlySet<string> = new Set(['registration_incomplete']);

/** whether a rejected request should be surfaced by the default handler */
function shouldShowDefaultToast(error: ClientError, skipDefaultErrorHandler?: boolean): boolean {
  if (skipDefaultErrorHandler === true) return false;
  return !QUIET_ERROR_CODES.has(String(error.responseJson?.code));
}

export interface Session {
  token: string;
  secret: string;
}

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';
export type ResponseData = { [key: string]: any };
export type QueryParams = { [key: string]: any };

export interface RequestOptions {
  headers?: HeadersInit;
  signal?: AbortSignal;
  skipDefaultErrorHandler?: boolean;
  onUploadProgress?: (loaded: number, total: number) => void;
}

export function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'AbortError';
}

/* -------------------------------- STATE -------------------------------- */

export const hostname = import.meta.env.VITE_PJAS_HOSTNAME ?? window.location.protocol + '//' + window.location.host;
let session: Session | undefined = restoreSession();

/* -------------------------------- ENCODING -------------------------------- */

function calculateShift(passphrase: string): number {
  return passphrase.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 26;
}

function pseudoEncode(text: string, passphrase: string): string {
  const shift = calculateShift(passphrase);
  return pseudoCaesarShift(text, shift, true);
}

function pseudoDecode(text: string, passphrase: string): string {
  const shift = calculateShift(passphrase);
  return pseudoCaesarShift(text, shift, false);
}

function pseudoCaesarShift(text: string, shift: number, encode: boolean): string {
  const aCharCode = 'a'.charCodeAt(0);
  const zCharCode = 'z'.charCodeAt(0);
  const ACharCode = 'A'.charCodeAt(0);
  const ZCharCode = 'Z'.charCodeAt(0);

  const shiftedText = text
    .split('')
    .map((char) => {
      const charCode = char.charCodeAt(0);

      if (charCode >= ACharCode && charCode <= ZCharCode) {
        const newCharCode = encode
          ? ((charCode - ACharCode + shift) % 26) + ACharCode
          : ((charCode - ACharCode - shift + 26) % 26) + ACharCode;
        return String.fromCharCode(newCharCode);
      }

      if (charCode >= aCharCode && charCode <= zCharCode) {
        const newCharCode = encode
          ? ((charCode - aCharCode + shift) % 26) + aCharCode
          : ((charCode - aCharCode - shift + 26) % 26) + aCharCode;
        return String.fromCharCode(newCharCode);
      }

      return char;
    })
    .join('');

  return shiftedText;
}

/* -------------------------------- FINGERPRINT -------------------------------- */

function createMiniBrowserFingerprint(): string {
  const userAgent = navigator.userAgent;
  const timezoneOffset = new Date().getTimezoneOffset();

  // Combine the collected data
  const data = `${userAgent}|${timezoneOffset}`;

  // Hash the data to create a fingerprint
  return enc.Base64.stringify(MD5(data));
}

/* -------------------------------- FUNCTIONS -------------------------------- */

function storeSession(session: Session) {
  localStorage.setItem(
    'ylcsession',
    pseudoEncode(enc.Base64.stringify(enc.Utf8.parse(JSON.stringify(session))), createMiniBrowserFingerprint())
  );
}

const LOGIN_REQUIRED_RELOAD_KEY = 'ylc_login_required_reload';
const LOGIN_REQUIRED_RELOAD_WINDOW_MS = 10_000;

/** reloads once; a second login-required 401 right after a reload means the boot flow did not redirect, so go to login */
function reloadOrLeaveForLogin() {
  let reloadedRecently = false;
  try {
    const last = Number(sessionStorage.getItem(LOGIN_REQUIRED_RELOAD_KEY));
    reloadedRecently = Date.now() - last < LOGIN_REQUIRED_RELOAD_WINDOW_MS;
    sessionStorage.setItem(LOGIN_REQUIRED_RELOAD_KEY, String(Date.now()));
  } catch {
    // storage unavailable: fall back to a plain reload
  }

  if (!reloadedRecently) {
    window.location.reload();
    return;
  }

  const { pathname, search } = window.location;
  window.location.assign(`/login?redirect=${encodeURIComponent(pathname + search)}`);
}

export function destroySession() {
  localStorage.removeItem('ylcsession');
}

function restoreSession(): Session | undefined {
  try {
    const sessionString = localStorage.getItem('ylcsession');
    if (!sessionString) {
      return undefined;
    }

    // Make sure createMiniBrowserFingerprint returns a valid string
    const fingerprint = createMiniBrowserFingerprint();
    if (typeof fingerprint !== 'string' || fingerprint.length === 0) {
      return undefined;
    }

    const decodedSessionString = pseudoDecode(sessionString, fingerprint);

    // Make sure the decoded session string is valid before parsing
    if (typeof decodedSessionString !== 'string' || decodedSessionString.length === 0) {
      return undefined;
    }

    const sessionData = enc.Base64.parse(decodedSessionString);
    const sessionUtf8 = enc.Utf8.stringify(sessionData);

    // Make sure JSON.parse doesn't throw an error and the parsed object is a valid session
    const session = JSON.parse(sessionUtf8);

    if (isValidSession(session)) {
      return session;
    }
  } catch (error) {
    console.error('Error while restoring session:', error);
  }

  return undefined;
}

function isValidSession(session: any): boolean {
  return session && session.token && session.secret;
}

/**
 * `rawBody` must be the exact string sent on the wire, since the server hashes the raw bytes.
 * Multipart bodies are signed as empty, as the server does.
 */
function buildSignedToken(method: string, path: string, session: Session, rawBody = ''): string {
  const timestamp = Math.floor(Date.now() / 1000);
  const bodyHash = SHA256(rawBody).toString();
  const payload = `${method}:${path}:${timestamp}:${bodyHash}`;
  const token = {
    token: session.token,
    timestamp: timestamp,
    v: SIGNATURE_VERSION_BODY_HASH,
    signature: HmacSHA256(payload, session.secret).toString(),
  };

  return enc.Base64.stringify(enc.Utf8.parse(JSON.stringify(token)));
}

export function createAccessTokenForUri(method: string, uri: string, rawBody?: string): string | undefined {
  if (!session) {
    return undefined;
  }

  return buildSignedToken(method, uri, session, rawBody);
}

function httpBuildQuery(data: { [key: string]: any }) {
  // set the locale if not already set
  if (!data.locale) {
    data.locale = currentLanguage;
  }

  const ret: string[] = [];
  for (const d in data) ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));

  return ret.join('&');
}

export function requestSessionToken(): Promise<Session> {
  return request('POST', '/v1/account/session');
}

export function createResetPasswordSession(body: { email: string; reset_token: string }): Promise<{ data: User }> {
  return request('POST', '/v1/account/password_reset/token_signin', {}, body);
}

export function authorizeClient(): Promise<Session> {
  return new Promise<Session>((resolve, reject) => {
    if (session) {
      resolve(session);
      return;
    }

    requestSessionToken()
      .then((ns) => {
        session = ns;
        storeSession(session);
        resolve(session);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function request(
  method: string,
  uri: string,
  parameters?: { [key: string]: any },
  body?: object | [any],
  requestOptions: RequestOptions = {}
) {
  const fetchOptions: RequestInit = {
    method,
    signal: requestOptions.signal,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(requestOptions.headers ?? {}),
    },
  };

  // signed as-is, never re-serialized
  let rawBody: string | undefined;

  // if not a get request encode the body to json
  if (method !== 'GET' && body) {
    if (body instanceof FormData) {
      // We are uploading files
      fetchOptions.body = body;
      // We delete content-type, it will autogenerate multipart header
      delete fetchOptions.headers;
    } else {
      // when body is json
      rawBody = JSON.stringify(body);
      fetchOptions.body = rawBody;
    }
  }

  const fullUri = `${uri}?${httpBuildQuery(parameters ?? {})}`;

  // apply the ssession token if available
  if (session) {
    if (!fetchOptions.headers) fetchOptions.headers = {};
    fetchOptions.headers['X-AccessToken'] = buildSignedToken(method, fullUri, session, rawBody);
  }
  const promise = fetch(hostname + fullUri, fetchOptions)
    .then(async (response) => {
      if (response.status >= 300) {
        let data: ResponseData = {};
        data = await response.json();

        const clientError = new ClientError(response.status, response, data);

        // if at any point in time we recieve a 401, we an invalid
        // access token we try again
        if (response.status === 401) {
          if (data.code === UNAUTHORIZED_CODE_NOT_AUTHENTICATED) {
            destroySession();
            // not a user-initiated leave, so unload guards stay quiet
            markIntentionalReload();
            window.location.reload();
          }

          // This happens when the Vue.js components send API requests when the user is not logged in.
          // The device session itself is still valid, so we don't destroy it - just reload, so App.vue's
          // boot flow re-evaluates auth and redirects to /login?redirect=<currentPath>.
          if (data.code === UNAUTHORIZED_CODE_LOGIN_REQUIRED) {
            markIntentionalReload();
            reloadOrLeaveForLogin();
          }
        }

        throw clientError;
      }

      if (response.headers.get('Content-Type')?.includes('json')) return await response.json();
      if (response.headers.get('Content-Type')?.includes('text')) return await response.text();
      return await response.blob();
    })
    .catch((error) => {
      if (isAbortError(error)) {
        throw error;
      }

      // forwards the error to the caller
      if (error instanceof ClientError) {
        if (shouldShowDefaultToast(error, requestOptions?.skipDefaultErrorHandler)) {
          const toast = useToast();
          toast.error(error.responseJson?.message ?? error.message);
        }

        throw error;
      }

      // a non-ClientError means the request never got a response at all (e.g. blocked by CORS,
      // DNS/network failure) — this used to be swallowed here, silently resolving the whole
      // request() call to undefined and leaving every caller to crash confusingly on
      // `response.data`/`response.foo` instead of seeing the real underlying error
      console.error('Request failure: ', error);
      if (requestOptions?.skipDefaultErrorHandler !== true) {
        const toast = useToast();
        toast.error(translator.get('common.network_error'));
      }
      throw error;
    });

  return promise;
}

/**
 * Unsigned request for the token-secured public pages (candidate upload and chat links). It skips the
 * session signature, the 401 handling and the default toast, since those pages render their own errors.
 * Rejects with a `ClientError` for error responses and with the raw error when no response arrived.
 */
export async function publicRequest<T = ResponseData>(
  method: RequestMethod,
  uri: string,
  body?: object | FormData
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const fetchOptions: RequestInit = { method, headers };

  if (body instanceof FormData) {
    fetchOptions.body = body;
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${hostname}${uri}?${httpBuildQuery({})}`, fetchOptions);
  const data: ResponseData = await response.json().catch(() => ({}));

  if (response.status >= 300) {
    throw new ClientError(response.status, response, data);
  }

  return data as T;
}

export function requestMultipart(
  method: string,
  uri: string,
  parameters?: { [key: string]: any },
  formData?: FormData,
  requestOptions: RequestOptions = {}
) {
  const fetchOptions: RequestInit = {
    method,
    signal: requestOptions.signal,
  };

  fetchOptions.body = formData;

  const fullUri = `${uri}?${httpBuildQuery(parameters ?? {})}`;

  // fetch() has no upload-progress API — fall through to XHR when the caller opts in
  if (requestOptions.onUploadProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, hostname + fullUri);
      if (session) {
        // multipart is signed as an empty body, matching the server
        xhr.setRequestHeader('X-AccessToken', buildSignedToken(method, fullUri, session));
      }
      if (requestOptions.signal) {
        if (requestOptions.signal.aborted) {
          xhr.abort();
          return reject(new DOMException('The operation was aborted.', 'AbortError'));
        }
        requestOptions.signal.addEventListener(
          'abort',
          () => {
            xhr.abort();
            reject(new DOMException('The operation was aborted.', 'AbortError'));
          },
          { once: true }
        );
      }
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) requestOptions.onUploadProgress!(e.loaded, e.total);
      });
      xhr.onload = () => {
        let data: any = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          /* non-JSON body */
        }
        if (xhr.status >= 300) {
          return reject(new ClientError(xhr.status, xhr as unknown as Response, data));
        }
        resolve(data);
      };
      xhr.onerror = () => reject(new ClientError(xhr.status || 0, xhr as unknown as Response, {}));
      xhr.send(formData);
    });
  }

  // apply the ssession token if available
  if (session) {
    if (!fetchOptions.headers) fetchOptions.headers = {};
    // multipart is signed as an empty body, matching the server
    fetchOptions.headers['X-AccessToken'] = buildSignedToken(method, fullUri, session);
  }

  const promise = fetch(hostname + fullUri, fetchOptions)
    .then(async (response) => {
      if (response.status >= 300) {
        let data = {};
        data = await response.json();
        throw new ClientError(response.status, response, data);
      }
      return await response.json();
    })
    .catch((error) => {
      if (isAbortError(error)) {
        throw error;
      }

      throw error;
    });

  return promise;
}
